// You vs one player: the record, the last five, what's on with them today (your turn, today's
// result, or a challenge to send them), averages and widest wins, and every challenge you both
// finished, each opening its challenge.
import { useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import type { Challenge, ChallengePlayer, Rivalry } from '@wbwl/contract'
import { NameBadge } from '../components/Badge.tsx'
import { Form, recordText } from '../components/challenge-ui.tsx'
import { Icon } from '../components/Icon.tsx'
import { DayCard, TurnCard } from '../components/rivals-ui.tsx'
import { Avatar, ErrorNote, Loading, NotFound, useToast } from '../components/shell.tsx'
import { ApiFailure, api } from '../lib/api.ts'
import { useEdition, useMe, useMyChallenges, usePlay, useRivalry } from '../lib/data.ts'
import { fmt, shortDate, weekday } from '../lib/game.ts'
import { dayOf, type Day } from '../lib/rivals.ts'
import { invalidate, setResource } from '../lib/store.ts'

export default function RivalryPage() {
  const id = useParams().id ?? ''
  const q = useRivalry(id)
  const me = useMe().data
  const navigate = useNavigate()
  const entry = useLocation().key === 'default'
  if (q.error) {
    if (q.error instanceof ApiFailure && q.error.status === 404) return <NotFound />
    return <div className="page"><ErrorNote error={q.error} retry={() => q.reload()} /></div>
  }
  if (!q.data || !me) return <div className="page"><Loading label="Looking up the record…" /></div>
  const r = q.data
  const name = r.player.displayName
  const rec = recordText(r.record, name)
  const recent = r.matches.slice(0, 5).reverse()
  const first = r.matches[r.matches.length - 1]

  return (
    <div className="page rivalry">
      <div className="topbar">
        <button type="button" className="ibtn" aria-label="Back" onClick={() => (entry ? navigate('/rivals') : navigate(-1))}><Icon name="back" /></button>
        <div className="title"><b className="ellipsis">You vs {name}</b><small>{r.matches.length} CHALLENGE{r.matches.length === 1 ? '' : 'S'} · SINCE NO. {first.edition}</small></div>
      </div>

      <section className="card rv-hero">
        <div className="rv-faces">
          <div className="rv-face"><Avatar id={me.id} name={me.displayName} src={me.avatar} size={56} />You</div>
          <div className="rv-score">{r.record.wins}<span>–</span>{r.record.losses}</div>
          {/* Their side opens their profile (a guest has none). */}
          {r.player.profile
            ? <Link className="rv-face" to={`/p/${r.player.profile}`} aria-label={`${name}’s profile`}><Avatar id={r.player.id} name={name} src={r.player.avatar} size={56} /><span className="named"><span className="ellipsis">{name}</span><NameBadge badge={r.player.badge} size={18} /></span></Link>
            : <div className="rv-face"><Avatar id={r.player.id} name={name} src={r.player.avatar} size={56} /><span className="ellipsis">{name}</span></div>}
        </div>
        <div className="rv-lead">{rec.lead}{r.record.ties ? ` · ${r.record.ties} tie${r.record.ties === 1 ? '' : 's'}` : ''}</div>
        <div className="rv-form">
          <Form form={r.record.form} big />
          <div className="nums">{recent.map((m) => <span key={m.edition}>{m.edition}</span>)}</div>
        </div>
      </section>

      <TodayWith rival={r.player} />

      <div className="stat-grid">
        <div className="stat"><b>{fmt(r.average.mine)}</b><span>your average<br />{name} {fmt(r.average.theirs)}</span></div>
        <div className="stat"><b>{r.biggestWin ? `+${fmt(r.biggestWin.margin)}` : '—'}</b><span>your biggest<br />{r.biggestWin ? `win, No. ${r.biggestWin.edition}` : 'win: not yet'}</span></div>
        <div className="stat"><b>{r.biggestLoss ? `+${fmt(r.biggestLoss.margin)}` : '—'}</b><span>{name}’s biggest<br />{r.biggestLoss ? `win, No. ${r.biggestLoss.edition}` : 'win: not yet'}</span></div>
      </div>

      <section className="you-sec">
        <div className="sec-head"><h3>Every challenge</h3><span className="eyebrow">newest first</span></div>
        <div className="hist">
          {r.matches.map((m) => {
            const result = m.mine > m.theirs ? 'w' : m.mine < m.theirs ? 'l' : 'd'
            return (
              <Link key={m.edition} className="hist-row" to={`/c/${m.code}`}>
                <div><b>No. {m.edition}</b><span className="faint mono small">{shortDate(m.date)}</span></div>
                <span className="sc"><b>{fmt(m.mine)}</b> – {fmt(m.theirs)}</span>
                <span className={`chip sm res-chip ${result}`}>{result === 'w' ? `Won ${fmt(m.mine - m.theirs)}` : result === 'l' ? `Lost ${fmt(m.theirs - m.mine)}` : 'Tied'}</span>
                <Icon name="next" size={16} />
              </Link>
            )
          })}
        </div>
      </section>
    </div>
  )
}

/** Today with them: your turn against their score, how today went between you, or a challenge to send. */
function TodayWith({ rival }: { rival: Rivalry['player'] }) {
  const info = useEdition('today').data
  const play = usePlay(info?.number ?? null).data
  const mine = useMyChallenges().data
  const [busy, setBusy] = useState(false)
  const navigate = useNavigate()
  const toast = useToast()
  if (!info || !mine) return null
  const group = mine.editions.find((g) => g.edition === info.number)
  const day = group ? dayOf(group) : null
  const them: ChallengePlayer | null = day?.players.find((p) => p.id === rival.id) ?? null
  const done = play?.phase === 'done'
  const name = rival.displayName

  async function join(then: string) {
    await Promise.all((day?.invites ?? []).map((c) => api.post(`/challenges/${c.code}/join`).catch(() => {})))
    invalidate('mychallenges', `edchallenges:${info!.number}`, 'rivals')
    navigate(then)
  }
  async function challenge() {
    setBusy(true)
    try {
      let own = day?.own ?? null
      if (!own) {
        own = await api.post<Challenge>('/challenges', { edition: info!.number })
      }
      const updated = await api.post<Challenge>(`/challenges/${own.code}/invite`, { playerId: rival.id })
      setResource(`challenge:${updated.code}`, updated)
      invalidate('mychallenges', `edchallenges:${info!.number}`)
      toast(<><Icon name="check" />Sent to {name}</>)
    } catch (e) {
      toast(e instanceof ApiFailure && e.body.message ? e.body.message : 'Couldn’t send that. Try again.')
    } finally {
      setBusy(false)
    }
  }

  // Just the two of you, out of everyone you're up against today.
  const pair = (d: Day): Day => ({ ...d, players: d.players.filter((p) => p.mine || p.id === rival.id), fresh: d.fresh.filter((p) => p.id === rival.id), toBeat: d.toBeat.filter((p) => p.id === rival.id) })
  if (day && them && done) {
    return (
      <>
        <div className="sec-head rv-sec"><h3>Today</h3></div>
        <DayCard day={pair(day)} title={`No. ${info.number} · ${weekday(info.date)}`} onAccept={() => join(`/c/${(day.open ?? day.toAccept[0]).code}`)} />
      </>
    )
  }
  if (day?.yourTurn && them?.status === 'joined' && them.done) {
    return (
      <>
        <div className="sec-head rv-sec"><h3 className="turn">Your turn</h3></div>
        <TurnCard day={pair(day)} today onPlay={() => join('/play')} />
      </>
    )
  }
  if (!them && done) {
    return (
      <section className="card rc ask rv-ask">
        <div className="ask-top">
          <Avatar id={rival.id} name={name} src={rival.avatar} size={40} />
          <div><b>{name} hasn’t played No. {info.number}</b><p>Send it in the app. They’ll see it on Today and in Rivals, no link needed.</p></div>
        </div>
        <button type="button" className="btn gold block" disabled={busy} onClick={challenge}><Icon name="swords" />Challenge {name}</button>
      </section>
    )
  }
  return null
}
